import React from 'react';
import { webOrigin } from '../../../utils/brand';
import BrandedQR from '../../BrandedQR';
import { LISTED_GRAY, MUTED } from '../proUi';
import { money, monthDay, hhmm } from './vendorUi';

const valueLine = (o) => {
  if (o.valueText) return o.valueText.toUpperCase();
  if (o.valueCents) return `${money(o.valueCents)} VALUE`;
  return null;
};
const stockLine = (o) => (o.remaining == null ? 'NO LIMIT' : `${o.remaining} LEFT`);

/**
 * One card component, three states (Vendor Menu and Offers §M9): claimable with a photo (168px
 * image), claimable without (starts at the text), and claimed (inverted to black, the code is
 * the only 26px type on the surface). Portal never takes money for an offer.
 */
export default function VendorOfferCard({ offer, onClaim, onRelease, busy = false, canClaimHint }) {
  const claim = offer.viewerClaim;
  if (claim && claim.state === 'active') {
    return (
      <div className="flex flex-col border-2 border-black bg-black" data-testid="offer-card-claimed">
        <div className="flex flex-col" style={{ padding: '14px 16px', gap: 9 }}>
          <span className="font-mono font-bold uppercase" style={{ fontSize: 10, letterSpacing: '0.1em', color: '#22C55E' }}>CLAIMED · IN YOUR WALLET</span>
          <span className="font-mono text-white" style={{ fontSize: 16, lineHeight: 1.3 }}>{offer.title}</span>
          <span className="font-mono" style={{ fontSize: 12, lineHeight: 1.5, color: LISTED_GRAY }}>Show this at the table. It comes off the bill there — nothing is charged through Portal.</span>
        </div>
        <div className="flex flex-col items-center" style={{ borderTop: '2px solid #fff', padding: 16, gap: 6 }}>
          <span className="font-mono font-bold uppercase" style={{ fontSize: 10, letterSpacing: '0.1em', color: LISTED_GRAY }}>SHOW THIS CODE</span>
          <span className="font-mono font-bold text-white" style={{ fontSize: 26, letterSpacing: '0.14em' }} data-testid="claim-code">{claim.code}</span>
        </div>
        <div className="flex items-center justify-between" style={{ borderTop: '2px solid #fff', padding: '12px 16px', gap: 10 }}>
          <span className="font-mono uppercase" style={{ fontSize: 10, letterSpacing: '0.05em', color: LISTED_GRAY }}>{offer.expiresAt ? `EXPIRES ${monthDay(offer.expiresAt)}` : 'NO EXPIRY'}</span>
          <button type="button" onClick={() => onRelease?.(claim)} disabled={busy} className="font-mono font-bold uppercase text-white disabled:opacity-50" style={{ fontSize: 11, letterSpacing: '0.05em', padding: '9px 14px', border: '2px solid #fff', background: '#000' }}>GIVE IT BACK</button>
        </div>
      </div>
    );
  }
  const ended = offer.phase === 'ran_out' || offer.phase === 'ended';
  return (
    <div className="flex flex-col border-2 border-black bg-white" style={{ height: '100%' }} data-testid="offer-card">
      {offer.photoUrl && (
        <div className="border-b-2 border-black" style={{ height: 168, background: '#F5F5F5', overflow: 'hidden' }}>
          <img src={offer.photoUrl} alt="" className="h-full w-full object-cover" />
        </div>
      )}
      <div className="flex flex-col" style={{ padding: '14px 16px', gap: 9 }}>
        <span className="font-mono" style={{ fontSize: 16, lineHeight: 1.3, color: '#1A1A1A' }}>{offer.title}</span>
        <span className="flex items-center" style={{ gap: 10 }}>
          {valueLine(offer) && <span className="font-mono font-bold text-black" style={{ fontSize: 13 }}>{valueLine(offer)}</span>}
          {offer.expiresAt && <span className="font-mono uppercase" style={{ fontSize: 11, letterSpacing: '0.05em', color: MUTED }}>UNTIL {monthDay(offer.expiresAt)}</span>}
        </span>
        {offer.terms && <span className="font-mono" style={{ fontSize: 12, lineHeight: 1.5, color: MUTED }}>{offer.terms}</span>}
      </div>
      <div className="mt-auto flex items-center justify-between border-t-2 border-black" style={{ padding: '12px 16px', gap: 10 }}>
        <span className="font-mono uppercase" style={{ fontSize: 10, letterSpacing: '0.05em', color: LISTED_GRAY }}>{ended ? (offer.phase === 'ran_out' ? 'RAN OUT' : 'ENDED') : stockLine(offer)}</span>
        {!ended && (
          <button type="button" onClick={() => onClaim?.(offer)} disabled={busy || (!offer.canClaim && !canClaimHint)} title={!offer.canClaim && canClaimHint ? canClaimHint : undefined}
            className="font-mono font-bold uppercase border-2 border-black bg-black text-white transition-colors hover:bg-white hover:text-black disabled:opacity-50 disabled:hover:bg-black disabled:hover:text-white" style={{ fontSize: 12, letterSpacing: '0.05em', padding: '11px 20px' }}>
            {claim && claim.state === 'used' && offer.usageMode === 'one_per_guest' ? 'USED' : 'CLAIM'}
          </button>
        )}
      </div>
    </div>
  );
}

/** The mobile offer row (M10B): value · stock on one 12px line, full-width CLAIM. */
export function VendorOfferRow({ offer, onClaim, busy }) {
  const claim = offer.viewerClaim;
  if (claim && claim.state === 'active') {
    return (
      <div className="flex items-baseline justify-between border-2 border-black bg-white" style={{ padding: '10px 12px', gap: 10 }} data-testid="offer-row-claimed">
        <span className="font-mono" style={{ fontSize: 13, color: '#1A1A1A' }}>{claim.code} · {offer.title.slice(0, 22)}{offer.title.length > 22 ? '…' : ''}</span>
        <span className="font-mono font-bold text-black" style={{ fontSize: 13 }}>{offer.expiresAt ? `EXPIRES ${monthDay(offer.expiresAt)}` : 'NO EXPIRY'}</span>
      </div>
    );
  }
  return (
    <div className="flex border-2 border-black bg-white" data-testid="offer-row">
      <div className="flex flex-1 flex-col" style={{ padding: 12, gap: 7 }}>
        <span className="font-mono" style={{ fontSize: 14, lineHeight: 1.3, color: '#1A1A1A' }}>{offer.title}</span>
        <span className="font-mono font-bold text-black" style={{ fontSize: 12 }}>{[valueLine(offer), stockLine(offer)].filter(Boolean).join(' · ')}</span>
        <button type="button" onClick={() => onClaim?.(offer)} disabled={busy || !offer.canClaim} className="w-full border-2 border-black bg-black text-center font-mono font-bold uppercase text-white disabled:opacity-50" style={{ fontSize: 11, letterSpacing: '0.05em', padding: 10 }}>CLAIM</button>
      </div>
    </div>
  );
}

/**
 * The guest's own claim: the full card with the QR (Offers Ops O2) or the spent, greyed record.
 * `size` 176 on the detail page, 150 on the MY TICKETS list.
 */
export function ClaimCard({ claim, size = 176, onRelease, compact = false }) {
  const spent = claim.state === 'used';
  const dead = spent || claim.state === 'expired' || claim.state === 'ran_out';
  const fg = dead ? MUTED : '#fff';
  const sub = dead ? MUTED : LISTED_GRAY;
  const url = `${webOrigin()}/offer/${claim.id}`;
  if (compact && spent) {
    return (
      <div className="flex items-center bg-black" style={{ border: `2px solid ${MUTED}`, padding: 13, gap: 12 }} data-testid="claim-spent-row">
        <span className="flex items-center justify-center font-mono font-bold uppercase" style={{ flex: '0 0 52px', height: 52, border: `2px solid ${MUTED}`, fontSize: 8, letterSpacing: '0.05em', color: MUTED }}>SPENT</span>
        <span className="flex flex-col" style={{ gap: 3 }}>
          <span className="font-mono" style={{ fontSize: 13, lineHeight: 1.3, color: LISTED_GRAY }}>{claim.title}</span>
          <span className="font-mono uppercase" style={{ fontSize: 10, letterSpacing: '0.05em', color: MUTED }}>USED {monthDay(claim.usedAt)} · {claim.businessName}</span>
        </span>
      </div>
    );
  }
  return (
    <div className="flex flex-col bg-black" style={{ border: `2px solid ${dead ? MUTED : '#fff'}`, width: compact ? undefined : 340, maxWidth: '100%' }} data-testid={spent ? 'claim-card-spent' : 'claim-card'}>
      <div className="flex items-center justify-between" style={{ padding: compact ? '11px 13px' : '14px 16px', borderBottom: `2px solid ${dead ? MUTED : '#fff'}`, gap: 10 }}>
        <span className="font-mono font-bold uppercase" style={{ fontSize: 10, letterSpacing: '0.1em', color: spent ? LISTED_GRAY : dead ? MUTED : '#22C55E' }}>
          {spent ? `USED · ${monthDay(claim.usedAt)}, ${hhmm(claim.usedAt)}` : claim.state === 'expired' ? 'EXPIRED' : claim.state === 'ran_out' ? 'RAN OUT' : 'CLAIMED'}
        </span>
        <span className="font-mono uppercase" style={{ fontSize: 10, letterSpacing: compact ? '0.05em' : '0.1em', color: sub }}>{compact ? (claim.expiresAt ? `EXPIRES ${monthDay(claim.expiresAt)}` : '') : (claim.usageMode === 'unlimited_per_guest' ? 'UNLIMITED PER GUEST' : 'ONE PER GUEST')}</span>
      </div>
      <div className="flex flex-col" style={{ padding: compact ? 13 : 16, gap: compact ? 6 : 8 }}>
        <span className="font-mono" style={{ fontSize: compact ? 14 : 16, lineHeight: 1.3, color: fg }}>{claim.title}</span>
        <span className="font-mono uppercase" style={{ fontSize: compact ? 11 : 12, letterSpacing: '0.05em', color: sub }}>{claim.businessName}{claim.businessCity && !compact ? ` · ${claim.businessCity}` : ''}</span>
      </div>
      <div className="flex flex-col items-center" style={{ borderTop: `2px solid ${dead ? MUTED : '#fff'}`, padding: compact ? '16px 13px' : '20px 16px', gap: compact ? 10 : 12 }}>
        <span className="font-mono font-bold uppercase" style={{ fontSize: compact ? 9 : 10, letterSpacing: '0.1em', color: sub }}>SHOW THIS AT THE TABLE</span>
        {dead ? (
          <span className="flex items-center justify-center font-mono font-bold uppercase" style={{ width: size, height: size, border: `2px solid ${MUTED}`, fontSize: 13, letterSpacing: '0.1em', color: MUTED }}>{spent ? 'SPENT' : claim.state === 'expired' ? 'EXPIRED' : 'RAN OUT'}</span>
        ) : (
          <div className="bg-white" style={{ padding: 6 }}><BrandedQR value={url} size={size - 12} /></div>
        )}
        <span className="font-mono font-bold" style={{ fontSize: compact ? 19 : 22, letterSpacing: '0.14em', color: fg, textDecoration: spent ? 'line-through' : 'none' }} data-testid="claim-code">{claim.code}</span>
      </div>
      <div className="flex items-center justify-between" style={{ borderTop: `2px solid ${dead ? MUTED : '#fff'}`, padding: '12px 16px', gap: 10 }}>
        {spent ? (
          <span className="font-mono" style={{ fontSize: 11, lineHeight: 1.5, color: MUTED }}>The QR dies the moment it is scanned. It stays in the wallet as a record, greyed, so the guest can see where it went.</span>
        ) : (
          <>
            <span className="font-mono uppercase" style={{ fontSize: 10, letterSpacing: '0.05em', color: sub }}>{claim.expiresAt ? `EXPIRES ${monthDay(claim.expiresAt)}` : 'NO EXPIRY'}</span>
            {!dead && onRelease && claim.isMine && <button type="button" onClick={() => onRelease(claim)} className="font-mono font-bold uppercase text-white" style={{ fontSize: 11, letterSpacing: '0.05em', padding: '9px 14px', border: '2px solid #fff', background: '#000' }}>GIVE IT BACK</button>}
          </>
        )}
      </div>
    </div>
  );
}

/** Black strip on the mobile Pro page: N OFFERS HERE · SEE THEM. Only when a live offer exists. */
export function OffersStrip({ count, onSee }) {
  if (!count) return null;
  return (
    <div className="flex items-center justify-between border-2 border-black bg-black" style={{ padding: 12, gap: 10 }} data-testid="offers-strip">
      <span className="flex flex-col" style={{ gap: 3, minWidth: 0 }}>
        <span className="font-mono font-bold uppercase text-white" style={{ fontSize: 11, letterSpacing: '0.05em' }}>{count} OFFER{count === 1 ? '' : 'S'} HERE</span>
        <span className="font-mono" style={{ fontSize: 10, color: LISTED_GRAY }}>Claim one before you go.</span>
      </span>
      <button type="button" onClick={onSee} className="font-mono font-bold uppercase text-black" style={{ flex: '0 0 auto', fontSize: 10, letterSpacing: '0.05em', border: '2px solid #fff', background: '#fff', padding: '9px 12px' }}>SEE THEM</button>
    </div>
  );
}
